import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Tag,
  Space,
  Select,
  message,
  Tooltip,
  Switch,
  Typography,
} from "antd";
import { Eye, Pencil } from "lucide-react";
import { EditOutlined, SearchOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import useDebounce from "../hooks/useDebounce";
import apiPath from "../constants/apiPath";

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;

function ContentManager() {
  const { request } = useRequest();
  const requestRef = useRef(request);

  // State management
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({
    sort: "newest",
  });
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [viewContent, setViewContent] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  // Loading states for different operations
  const [updateLoading, setUpdateLoading] = useState(false);

  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  const debouncedSearchText = useDebounce(searchText, 500);

  // Fetch content pages
  const fetchContents = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();

    // Add sort parameter
    if (filters.sort) {
      params.append("sort", filters.sort);
    }

    // Add search parameter
    if (debouncedSearchText) {
      params.append("search", debouncedSearchText);
    }

    // Add pagination parameters
    params.append("page", pagination.current);
    params.append("limit", pagination.pageSize);

    requestRef.current({
      url: `/api/admin/content`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);

        if (data?.status === 200 && data?.data) {
          setContents(Array.isArray(data.data) ? data.data : []);
        } else {
          setContents([]);
          ShowToast(
            data?.message || "Failed to fetch content pages",
            Severty.ERROR
          );
        }
      },
      onError: (error) => {
        setLoading(false);
        setContents([]);
        ShowToast("Failed to fetch content pages", Severty.ERROR);
      },
    });
  }, [filters, debouncedSearchText, pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchContents();
  }, [fetchContents]);

  const handleViewContent = (content) => {
    setViewModalVisible(true);
    setViewContent(null);

    requestRef.current({
      url: `/api/admin/content/${content.id}`,
      method: "GET",
      onSuccess: (res) => {
        if (res?.status === 200 && res?.data) {
          setViewContent(res.data);
        } else {
          ShowToast("Failed to load content", Severty.ERROR);
        }
      },
      onError: () => {
        ShowToast("Failed to load content", Severty.ERROR);
      },
    });
  };

  // Handlers
  const handleTableChange = (paginationInfo, filters, sorter) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleEditContent = (content) => {
    setSelectedContent(content);
    setModalVisible(true);

    // Fetch full content details
    requestRef.current({
      url: `/api/admin/content/${content.id}`,
      method: "GET",
      onSuccess: (data) => {
        if (data?.status === 200 && data?.data) {
          const contentData = data.data;
          form.setFieldsValue({
            page_name: contentData.page_name,
            status: contentData.status === "PUBLISHED",
          });
          // Set content in editor
          form.setFieldValue("content", contentData.content);
        } else {
          ShowToast(
            data?.message || "Failed to fetch content details",
            Severty.ERROR
          );
        }
      },
      onError: (error) => {
        ShowToast("Failed to fetch content details", Severty.ERROR);
      },
    });
  };

  useEffect(() => {
    window.toggleFAQ = (id) => {
      const el = document.getElementById(id);
      if (!el) return;
      el.style.display = el.style.display === "none" ? "block" : "none";
    };

    return () => {
      delete window.toggleFAQ;
    };
  }, []);

  // Update content
  const handleFormSubmit = async (values) => {
    try {
      const contentData = {
        page_name: values.page_name,
        content: values.content,
        status: values.status ? "PUBLISHED" : "DRAFT",
      };

      setUpdateLoading(true);
      requestRef.current({
        url: `/api/admin/content/${selectedContent.id}`,
        method: "PUT",
        data: contentData,
        onSuccess: (data) => {
          if (data?.status === 200) {
            ShowToast("Content updated successfully", Severty.SUCCESS);
            setModalVisible(false);
            fetchContents();
          } else {
            ShowToast(
              data?.message || "Failed to update content",
              Severty.ERROR
            );
          }
          setUpdateLoading(false);
        },
        onError: (error) => {
          ShowToast("Failed to update content", Severty.ERROR);
          setUpdateLoading(false);
        },
      });
    } catch (error) {
      ShowToast("Failed to save content", Severty.ERROR);
      setUpdateLoading(false);
    }
  };

  // Table columns
  const getColumns = () => [
    {
      title: "S. No.",
      key: "serial_no",
      width: 80,
      render: (_, __, index) => {
        const serialNumber =
          (pagination.current - 1) * pagination.pageSize + index + 1;
        return <Text>{serialNumber}</Text>;
      },
    },
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      render: (id) => <Text code>PAGE_{id}</Text>,
    },
    // {
    //   title: "Page Key",
    //   dataIndex: "page_key",
    //   key: "page_key",
    //   render: (page_key) => <strong>{page_key}</strong>,
    // },
    {
      title: "Page Name",
      dataIndex: "page_name",
      key: "page_name",
      sorter: (a, b) => a.page_name.localeCompare(b.page_name),
      render: (page_name) => <strong>{page_name}</strong>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <Tag color={status === "PUBLISHED" ? "green" : "orange"}>{status}</Tag>
      ),
    },
    {
      title: "System Page",
      dataIndex: "is_system_page",
      key: "is_system_page",
      width: 120,
      render: (is_system_page) => (
        <Tag color={is_system_page ? "blue" : "default"}>
          {is_system_page ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
      key: "updated_at",
      width: 200,
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, record) => {
        return (
          <>
            <Space>
              <Tooltip color="#000" title="View">
                <Button
                  className="button-action-small"
                  onClick={() => handleViewContent(record)}
                  icon={<Eye />}
                />
              </Tooltip>

              <Tooltip color="#000" title="Edit">
                <Button
                  className="button-action-small"
                  onClick={() => handleEditContent(record)}
                  icon={<EditOutlined />}
                />
              </Tooltip>
            </Space>
          </>
        );
      }
    },
  ];

  return (
    <div>
      <Card
        title={
          <>
            <span>Content Manager</span>
            <p>Manage static content pages for the platform</p>
          </>
        }
        // extra={
        //   <Space wrap>
        //     <Search
        //       placeholder="Search content pages"
        //       value={searchText}
        //       onChange={(e) => setSearchText(e.target.value)}
        //       onSearch={handleSearch}
        //       style={{ width: 200 }}
        //     />
        //     <Select
        //       placeholder="Sort By"
        //       style={{ width: 120 }}
        //       value={filters.sort}
        //       onChange={(value) => handleFilterChange("sort", value)}
        //     >
        //       <Option value="newest">Newest</Option>
        //       <Option value="oldest">Oldest</Option>
        //     </Select>
        //   </Space>
        // }
      >
        {/* Contents Table */}
        <div className="table-responsive">
          <Table
            columns={getColumns()}
            dataSource={contents}
            rowKey="id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total || 0,
              showSizeChanger: true,
              showQuickJumper: false,
              showTotal: (total, range) =>
                `Showing ${range[0]}-${range[1]} of ${total} content pages`,
              pageSizeOptions: ["10", "20", "30", "50"],
            }}
            loading={loading}
            onChange={handleTableChange}
          />
        </div>
      </Card>

      {/* View Content Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            View Content: {viewContent?.page_name}
          </div>
        }
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
      >
        {!viewContent ? (
          <p>Loading...</p>
        ) : (
          <>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Tag color={viewContent.status === "PUBLISHED" ? "green" : "orange"}>
                  {viewContent.status}
                </Tag>
                {viewContent.is_system_page && (
                  <Tag color="blue" style={{ marginLeft: 8 }}>
                    System Page
                  </Tag>
                )}
              </Col>

              <Col span={24}>
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    padding: 16,
                    maxHeight: "60vh",
                    overflowY: "auto",
                    background: "#fafafa",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: viewContent.content,
                  }}
                />
              </Col>
            </Row>
          </>
        )}
      </Modal>

      {/* Edit Content Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            Edit Content: {selectedContent?.page_name}
          </div>
        }
        open={modalVisible}
        onCancel={() => !updateLoading && setModalVisible(false)}
        footer={null}
        width={800}
        confirmLoading={updateLoading}
      >
        <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
          <Form.Item
            name="page_name"
            label="Page Name"
            rules={[
              {
                required: true,
                message: "Please enter page name",
              },
              {
                max: 100,
                message: "Name must be 100 characters or less",
              },
            ]}
          >
            <Input placeholder="Enter page name" />
          </Form.Item>

          <Form.Item
            name="content"
            label="Content"
            rules={[
              {
                required: true,
                message: "Please enter content",
              },
            ]}
          >
            <ReactQuill theme="snow" style={{ height: "200px" }} />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            valuePropName="checked"
            initialValue={false}
            style={{ marginTop: "50px" }}
          >
            <Switch checkedChildren="Published" unCheckedChildren="Draft" />
          </Form.Item>

          <Form.Item
            style={{ marginBottom: 0, textAlign: "right", marginTop: "20px" }}
          >
            <Button
              style={{ marginRight: 8 }}
              onClick={() => setModalVisible(false)}
              disabled={updateLoading}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={updateLoading}
              disabled={updateLoading}
            >
              Update
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default ContentManager;
